import React from 'react';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { Badge } from '@oysterjs/ui/badge';
import { Checkbox } from '@oysterjs/ui/checkbox';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import { TableHeader } from '@oysterjs/ui/table';
import {
  CallFieldsFragment,
  CallSentiment,
  CallStatus,
  CallSortField,
  CallSortInput,
  SortDirection
} from '~/api/graphql';
import { CallSeenIndicator } from './call-seen';

export type CallSortKey =
  | 'customer'
  | 'phone'
  | 'status'
  | 'sentiment'
  | 'endReason'
  | 'duration'
  | 'date';

const SORT_KEY_TO_FIELD: Record<CallSortKey, CallSortField> = {
  customer: CallSortField.Customer,
  phone: CallSortField.FromNumber,
  status: CallSortField.Status,
  sentiment: CallSortField.Sentiment,
  endReason: CallSortField.DisconnectionReason,
  duration: CallSortField.Duration,
  date: CallSortField.StartedAt
};

/** Map UI sort key + direction to GraphQL CallSortInput (default: STARTED_AT DESC) */
export function callSortToCallSortInput(
  sortBy: CallSortKey | undefined,
  sortDirection: 'asc' | 'desc'
): CallSortInput {
  const field = sortBy ? SORT_KEY_TO_FIELD[sortBy] : CallSortField.StartedAt;
  const direction = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
  return { field, direction };
}

export const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const dateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

export const getCallDurationSeconds = (call: CallFieldsFragment): number =>
  Math.round(
    (new Date(call.endedAt || new Date()).getTime() - new Date(call.startedAt).getTime()) / 1000
  );

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

export const CallStatusBadge = (props: { status: CallStatus; className?: string }) => (
  <Badge
    // eslint-disable-next-line
    // @ts-ignore
    color={
      {
        [CallStatus.InProgress]: 'blue',
        [CallStatus.Completed]: 'green',
        [CallStatus.Error]: 'red',
        [CallStatus.Registered]: 'neutral'
      }[props.status] || 'neutral'
    }
    className={props.className}
  >
    {titleCase(props.status)}
  </Badge>
);

const SentimentBadge = (props: { sentiment: CallSentiment }) => (
  <Badge
    // eslint-disable-next-line
    // @ts-ignore
    color={
      {
        [CallSentiment.Positive]: 'green',
        [CallSentiment.Neutral]: 'neutral',
        [CallSentiment.Negative]: 'red'
      }[props.sentiment] || 'neutral'
    }
  >
    {titleCase(props.sentiment)}
  </Badge>
);

const Dash = () => <div className="text-neutral-500 dark:text-neutral-400">—</div>;

export function getCallColumns(opts: {
  selectedSet: Set<string>;
  setSelectedCalls: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: CallFieldsFragment[];
}): ColumnDef<CallFieldsFragment, CallSortKey>[] {
  const { selectedSet, setSelectedCalls, filtered } = opts;

  return [
    {
      id: 'checkbox',
      noHref: true,
      renderHeader: () => (
        <TableHeader className="w-0 py-1.5 px-3">
          <Checkbox
            checked={selectedSet.size > 0 && selectedSet.size === filtered.length}
            color="sky"
            onChange={(v) => setSelectedCalls(v ? filtered.map((c) => c.id) : [])}
          />
        </TableHeader>
      ),
      renderCell: (call) => (
        <Checkbox
          checked={selectedSet.has(call.id)}
          color="sky"
          onChange={(v) =>
            setSelectedCalls((prev) =>
              v ? [...new Set([...prev, call.id])] : prev.filter((id) => id !== call.id)
            )
          }
        />
      )
    },
    {
      id: 'customer',
      header: 'Customer',
      sortKey: 'customer',
      renderCell: (call) => (
        <div className="flex items-center">
          <div className="mr-2 flex-shrink-0">
            <CallSeenIndicator seenAt={call.seenAt} />
          </div>
          <div>
            {call.account?.account.name ? (
              <div className="flex items-center gap-1 font-medium">
                {call.account.account.name}
                <CheckBadgeIcon className="size-4 text-blue-400" />
              </div>
            ) : call.fromName ? (
              <div className="font-medium">{call.fromName}</div>
            ) : (
              <div className="font-medium text-neutral-500 dark:text-neutral-400">—</div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'phone',
      header: 'Phone Number',
      sortKey: 'phone',
      renderCell: (call) => call.fromNumber || <Dash />
    },
    {
      id: 'status',
      header: 'Status',
      sortKey: 'status',
      renderCell: (call) => <CallStatusBadge status={call.status} />
    },
    {
      id: 'sentiment',
      header: 'Sentiment',
      sortKey: 'sentiment',
      renderCell: (call) =>
        call.sentiment ? <SentimentBadge sentiment={call.sentiment} /> : <Dash />
    },
    {
      id: 'endReason',
      header: 'End Reason',
      sortKey: 'endReason',
      renderCell: (call) =>
        call.disconnectionReason ? titleCase(call.disconnectionReason) : <Dash />
    },
    {
      id: 'duration',
      header: 'Duration',
      sortKey: 'duration',
      renderCell: (call) => (call.endedAt ? formatDuration(getCallDurationSeconds(call)) : <Dash />)
    },
    {
      id: 'date',
      header: 'Timestamp',
      sortKey: 'date',
      renderCell: (call) => dateFormatter.format(new Date(call.startedAt))
    }
  ];
}
